import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useAdminsQuery } from "../services/useAdminsQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { Button, EmptyState, LoadingSkeleton, ErrorState } from "@a2z/ui";

type AdminAdminsPageClientProps = {
  locale: string;
};

export function AdminsPage({ locale }: AdminAdminsPageClientProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin =
    session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: admins, isLoading, error, refetch } = useAdminsQuery();

  if (isSessionLoading || (!isSuperAdmin && !isSessionLoading) || isLoading) {
    return <LoadingSkeleton variant="list" rows={5} />;
  }

  if (error || !admins) {
    return <ErrorState onRetry={refetch} />;
  }

  const roleLabels: Record<string, string> = {
    [ADMIN_ROLES.ADMIN]: t(keys.admin.roleAdmin),
    [ADMIN_ROLES.SUPER_ADMIN]: t(keys.admin.roleSuperAdmin),
    [ADMIN_ROLES.CUSTOMER_SERVICE]: t(keys.admin.roleCustomerService),
    [ADMIN_ROLES.SALES]: t(keys.admin.roleSales),
  };

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.admin.adminsTitle)}
          </h1>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => navigate(`/${locale}/admin/admins/new`)}
        >
          {t(keys.common.create)}
        </Button>
      </div>
      {admins.length === 0 ? (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.admin.adminsEmptySubtitle)}
        />
      ) : (
        <div className="grid gap-3">
          {admins.map((admin) => (
            <Link
              key={admin.id}
              to={`/${locale}/admin/admins/${admin.id}`}
              className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm transition hover:bg-[color:var(--hover)]"
            >
              <div className="grid gap-2 sm:grid-cols-2 sm:items-center sm:gap-4">
                <div>
                  <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                    {admin.name}
                  </div>
                  {admin.roles?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2 text-[13px] font-semibold text-[color:var(--text-secondary)]">
                      {admin.roles.map((role) => (
                        <span
                          key={role}
                          className="rounded-full bg-[color:var(--background-muted)] px-2 py-0.5"
                        >
                          {roleLabels[role] ?? role}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="text-xs text-[color:var(--text-muted)] sm:hidden">
                    {admin.email}
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)] sm:hidden">
                    {admin.phone}
                  </div>
                </div>
                <div className="hidden text-xs text-[color:var(--text-muted)] sm:flex sm:flex-col sm:gap-1">
                  <span>{admin.email}</span>
                  <span>{admin.phone}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
